import { useTranslation } from '@pancakeswap/localization'
import { Box, Card, Flex, Heading, NextLinkFromReactRouter, Text } from '@pancakeswap/uikit'
import { useMemo } from 'react'
import { DexId, VersionParam, useInfoRoute, useInfoSummary } from 'state/infoHub/hooks'
import BarChart from 'views/Info/components/InfoCharts/BarChart'
import LineChart from 'views/Info/components/InfoCharts/LineChart'
import HoverableChart from 'views/Info/components/InfoCharts/HoverableChart'
import { ChartCardsContainer } from 'views/Info/Overview'
import { ActivityBlock } from './ActivityBlock'
import { DataStateBar } from './DataStateBar'
import { History } from './History'
import { MarketHeader } from './MarketHeader'
import { ProtocolCards } from './ProtocolCards'
import { PoolStatTable, TokenStatTable } from './tables'
import { Transactions } from './Transactions'

/**
 * The overview of one DEX, PancakeSwap-Info order: stat tiles, the TVL and Volume charts, the top pools and tokens, the transactions feed.
 * Our own MultiSwap activity is one compact card at the end, never the page. `/info` (Total) renders this with the CADINUdex market and the
 * activity across all DEXes; `/info/[dex]` with the route's DEX.
 */
export const OverviewView: React.FC<{ marketDex?: DexId; activityScope?: 'total' | DexId; version?: VersionParam }> = ({ marketDex, activityScope, version: versionProp }) => {
  const { t, currentLanguage: { locale } } = useTranslation()
  const route = useInfoRoute()
  const dex = marketDex ?? route.dex
  const version = versionProp ?? route.version
  const scope = activityScope ?? dex
  const { summary, error, isLoading, retry } = useInfoSummary(dex, version)
  const { hrefFor } = route
  const currentDate = useMemo(() => new Date().toLocaleString(locale, { month: 'short', year: 'numeric', day: 'numeric' }), [locale])
  const protocolForChart = useMemo(() => {
    const p = summary?.protocol.data
    return p ? { volumeUSD: p.volume24hUSD ?? 0, volumeUSDChange: p.volume24hChange ?? 0, liquidityUSD: p.tvlUSD ?? 0, liquidityUSDChange: p.tvlChange24h ?? 0, txCount: p.txCount24h, txCountChange: p.txCount24hChange ?? 0 } : undefined
  }, [summary])
  if (!dex || !scope) return null
  const chart = summary?.chart
  return (
    <Box>
      <MarketHeader dex={dex} version={version}>
        <DataStateBar meta={summary?.protocol.meta} loading={isLoading} error={error} onRetry={retry} inline />
      </MarketHeader>
      <ProtocolCards data={summary?.protocol.data} pools={summary?.pools.data} meta={summary?.protocol.meta} loading={isLoading} version={version} withSparklines={dex === 'cadinu'} />
      {dex === 'cadinu' ? (
        <Box mt="20px">
          <History scope="protocol" version={version} />
        </Box>
      ) : (
        <>
          {chart?.data && chart.data.length > 0 && protocolForChart && (
            <Box mt="20px">
              <ChartCardsContainer>
                <Card>
                  <HoverableChart chartData={chart.data} protocolData={protocolForChart} currentDate={currentDate} valueProperty="liquidityUSD" title={t('Liquidity')} ChartComponent={LineChart} />
                </Card>
                <Card>
                  <HoverableChart chartData={chart.data} protocolData={protocolForChart} currentDate={currentDate} valueProperty="volumeUSD" title={t('Volume 24H')} ChartComponent={BarChart} />
                </Card>
              </ChartCardsContainer>
              <Text fontSize="12px" color="textSubtle" mt="4px">
                {t('History')}: {chart.meta.coverage} {chart.meta.warnings.join(' · ')}
              </Text>
            </Box>
          )}
          {chart && !chart.data && (
            <Box mt="12px">
              <DataStateBar meta={chart.meta} compact />
            </Box>
          )}
        </>
      )}
      <Flex justifyContent="space-between" alignItems="baseline" mt="32px" mb="12px">
        <Heading scale="md" as="h3">
          {t('Top pools')}
        </Heading>
        <NextLinkFromReactRouter to={hrefFor(dex, version, 'pools')}>{t('All pools')} →</NextLinkFromReactRouter>
      </Flex>
      <Card>
        <PoolStatTable pools={summary?.pools.data} meta={summary?.pools.meta} loading={isLoading} error={error} onRetry={retry} dex={dex} version={version} limit={5} />
      </Card>
      <Flex justifyContent="space-between" alignItems="baseline" mt="32px" mb="12px">
        <Heading scale="md" as="h3">
          {t('Top tokens')}
        </Heading>
        <NextLinkFromReactRouter to={hrefFor(dex, version, 'tokens')}>{t('All tokens')} →</NextLinkFromReactRouter>
      </Flex>
      <Card>
        <TokenStatTable tokens={summary?.tokens.data} meta={summary?.tokens.meta} loading={isLoading} error={error} onRetry={retry} dex={dex} version={version} limit={5} />
      </Card>
      <Transactions dex={dex} version={version} />
      <Box mt="32px">
        <ActivityBlock scope={scope} version={version} />
      </Box>
    </Box>
  )
}
export default OverviewView
